import * as React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Button,
  ButtonGroup,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Stack,
  TableContainer,
} from '@mui/material'

import {
  AddCircle,
  Archive,
  Error,
  Pageview,
  RemoveCircle,
} from '@mui/icons-material'
import { getCodeSetLabelLookup } from '../../../Functions/CodeSetFns'
import { listFromCodes } from '../../../Functions/ListItemGenerator'
import { Box } from '@mui/system'
import { ReadOnlyTextField } from '../../../garvan-react/Components/ReadOnlyTextField'
import { DataGridPro, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useUserContext } from '../../../Contexts/UserConfig'
import PageSection from '../../../Constants&Theme/PageSection'
import { stackGap } from '../../../Constants&Theme/Spacing'
import { ScreenManageDGSampleStatusChips } from '../ManageScreens/ScreenManageDGSampleStatusChips'
import { useGetCollectionCols } from './ScreenManageCollectionCols'
import { useSummariseStatus } from './StatusSummary'
import ConfirmAbortScreenDialog from './ConfirmAbortScreenDialog'
import { useUpdateScreenStatus } from '../../../APIs/Hooks/ScreenHooks'
import { useBulkUpdateSampleResult } from '../../../APIs/Hooks/SampleHooks'
import { useScreenContext } from '../../../Contexts/Modules/HealthModule/SelectedScreenModule'

function ScreenManageCollections() {
  const { whoAmI } = useUserContext()
  const navigate = useNavigate()
  const { loginConfig, loadingConfig } = useUserContext()
  const collectionCols = useGetCollectionCols()
  const { screenQuery } = useScreenContext()
  const statusSummary = useSummariseStatus(screenQuery?.data)
  const [openAbort, setOpenAbort] = useState(false)
  const [selectedSamples, setSelectedSamples] = useState<GridRowSelectionModel>(
    [],
  )
  const billingStatus = listFromCodes(
    loginConfig,
    'ScreenBillingStatusID',
    loadingConfig,
    screenQuery?.data?.BillingStatus,
  )

  const { mutate: statusUpdate } = useUpdateScreenStatus()
  const { mutate: resultUpdate, isLoading } = useBulkUpdateSampleResult(
    selectedSamples as number[],
  )

  return (
    <Box
      sx={{
        maxWidth: 'xl',
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <PageSection
        title={`Screen ${screenQuery?.data?.ScreenID ?? ''}`}
        loading={screenQuery?.isLoading}
        leftDecorator={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Pageview />}
            onClick={() => navigate('collections')}
          >
            View Original Request
          </Button>
        }
        rightDecorator={
          <Button
            variant="outlined"
            color="error"
            startIcon={<Archive />}
            disabled={
              screenQuery?.data?.CollectionStatus === 'CPT' ||
              screenQuery?.data?.CollectionStatus === 'ARC'
            }
            onClick={() => setOpenAbort(true)}
            sx={{ ml: 'auto' }}
          >
            Abort & Archive
          </Button>
        }
      >
        <Stack
          direction={'row'}
          width={1}
          gap={stackGap.row}
          sx={{ mt: 2 }}
        >
          <ReadOnlyTextField
            label="Request Name"
            value={screenQuery?.data?.Name || ''}
            style={{ flex: 3 }}
          />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel>Billing Status</InputLabel>
            <Select
              label="Billing Status"
              value={screenQuery?.data?.BillingStatus ?? ''}
              onChange={async (e) => {
                if (screenQuery?.data)
                  statusUpdate({
                    billing_status: e.target.value,
                    collection_status: screenQuery?.data.CollectionStatus,
                  })
              }}
              disabled={
                !loginConfig?.Modules?.EditScreenRequest ||
                !screenQuery?.data?.CollectionStatus
              }
            >
              {billingStatus}
            </Select>
          </FormControl>
          <ReadOnlyTextField
            label="Collection Status"
            value={
              getCodeSetLabelLookup(
                loginConfig,
                ['ScreenCollectionStatusID'],
                screenQuery?.data?.CollectionStatus,
              ) || ''
            }
            sx={{ flex: 1 }}
          />
        </Stack>
        <Box
          sx={{
            width: 1,
            display: 'flex',
            justifyContent: 'space-between',
            my: 1,
          }}
        >
          <ScreenManageDGSampleStatusChips
            {...({} as any)}
            value={statusSummary}
            direction={'row'}
          />
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ButtonGroup
              color="primary"
              sx={{ display: 'flex', ml: 'auto' }}
              disabled={isLoading}
            >
              <Button
                startIcon={<AddCircle />}
                disabled={selectedSamples.length === 0 || !whoAmI}
                onClick={() => resultUpdate('POS')}
              >
                Positive
              </Button>
              <Button
                startIcon={<Error />}
                disabled={selectedSamples.length === 0 || !whoAmI}
                onClick={() => resultUpdate('INC')}
              >
                Inconclusive
              </Button>
              <Button
                startIcon={<RemoveCircle />}
                disabled={selectedSamples.length === 0 || !whoAmI}
                onClick={() => resultUpdate('NEG')}
              >
                Negative
              </Button>
            </ButtonGroup>
            <FormHelperText>
              Mark all selected rows with this result
            </FormHelperText>
          </Box>
        </Box>
        <TableContainer sx={{ height: 800, width: 1 }}>
          <DataGridPro
            rows={
              screenQuery?.data?.Collections ?
                screenQuery.data.Collections.map((item: any) => {
                  return { ...item, id: item.SampleID }
                })
              : []
            }
            columns={collectionCols}
            loading={screenQuery?.isLoading}
            disableColumnSelector={true}
            checkboxSelection={true}
            onRowSelectionModelChange={setSelectedSamples}
            rowSelectionModel={selectedSamples}
            onRowClick={(props) =>
              navigate({ pathname: '/stuart/sample/info/' + props.id })
            }
          />
        </TableContainer>
        <ConfirmAbortScreenDialog
          open={openAbort}
          setOpen={setOpenAbort}
        />
      </PageSection>
    </Box>
  )
}

export default ScreenManageCollections
